<script setup lang="ts">
import { computed } from "vue";
import type { Product } from "../data/products";

const props = defineProps<{ product: Product; aspect?: string }>();

const seed = computed(() => {
  let h = 0;
  for (const c of props.product.id) h = (h * 31 + c.charCodeAt(0)) | 0;
  return Math.abs(h);
});

const aspectStyle = computed(() => ({
  aspectRatio: props.aspect ?? "4 / 5"
}));

const lines = computed(() => {
  const out: { x1: number; y1: number; x2: number; y2: number; opacity: number }[] = [];
  const s = seed.value;
  for (let i = 0; i < 60; i++) {
    const r = ((s >> (i % 8)) ^ (i * 37)) & 0xff;
    const y = (i / 60) * 100;
    const offset = (r / 255) * 6 - 3;
    out.push({
      x1: -2,
      y1: y,
      x2: 102,
      y2: y + offset,
      opacity: 0.05 + ((r % 32) / 32) * 0.18
    });
  }
  return out;
});

const dots = computed(() => {
  const out: { cx: number; cy: number; r: number; alpha: number }[] = [];
  const s = seed.value;
  for (let i = 0; i < 50; i++) {
    const a = ((s * (i + 1)) ^ (i * 911)) & 0xffff;
    out.push({
      cx: (a % 100),
      cy: ((a >> 4) % 100),
      r: 0.6 + ((a >> 8) % 6) / 10,
      alpha: 0.08 + ((a >> 2) % 24) / 100
    });
  }
  return out;
});
</script>

<template>
  <div class="art" :style="aspectStyle">
    <svg viewBox="0 0 100 125" preserveAspectRatio="xMidYMid slice" class="art__svg">
      <defs>
        <linearGradient :id="`grad-${product.id}`" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" :stop-color="product.art.base" />
          <stop offset="100%" :stop-color="product.art.accent" />
        </linearGradient>
        <radialGradient :id="`glow-${product.id}`" cx="0.7" cy="0.25" r="0.7">
          <stop offset="0%" stop-color="#ffd4c4" stop-opacity="0.45" />
          <stop offset="100%" stop-color="#000" stop-opacity="0" />
        </radialGradient>
      </defs>
      <rect width="100" height="125" :fill="`url(#grad-${product.id})`" />
      <rect width="100" height="125" :fill="`url(#glow-${product.id})`" />

      <g v-if="product.art.pattern === 'weave'" stroke="#ffffff" stroke-width="0.4">
        <line
          v-for="(line, i) in lines"
          :key="`l-${i}`"
          :x1="line.x1"
          :y1="line.y1"
          :x2="line.x2"
          :y2="line.y2"
          :opacity="line.opacity"
        />
      </g>

      <g v-else-if="product.art.pattern === 'knit'" stroke="#fff" stroke-width="0.5">
        <path
          v-for="i in 18"
          :key="`k-${i}`"
          :d="`M -2 ${i * 7} Q 25 ${i * 7 - 4} 50 ${i * 7} T 102 ${i * 7}`"
          fill="none"
          opacity="0.18"
        />
      </g>

      <g v-else-if="product.art.pattern === 'wave'" stroke="#fff" stroke-width="0.6">
        <path
          v-for="i in 10"
          :key="`w-${i}`"
          :d="`M -2 ${20 + i * 9} C 30 ${10 + i * 9} 70 ${30 + i * 9} 102 ${20 + i * 9}`"
          fill="none"
          :opacity="0.06 + i * 0.02"
        />
      </g>

      <g v-else-if="product.art.pattern === 'satin'">
        <rect
          v-for="i in 8"
          :key="`s-${i}`"
          :x="-5 + i * 14"
          y="-10"
          width="6"
          height="140"
          fill="#ffffff"
          :opacity="0.04 + (i % 3) * 0.03"
          transform="rotate(20 50 60)"
        />
      </g>

      <g v-else>
        <circle
          v-for="(d, i) in dots"
          :key="`d-${i}`"
          :cx="d.cx"
          :cy="d.cy"
          :r="d.r"
          fill="#fff"
          :opacity="d.alpha"
        />
      </g>

      <rect width="100" height="125" fill="url(#noiseMask)" opacity="0.08" />
    </svg>
    <div class="art__chip" v-if="product.badge">{{ product.badge }}</div>
    <div class="art__name">
      <span>{{ product.name.toUpperCase() }}</span>
    </div>
  </div>
</template>

<style scoped>
.art {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #1a0606;
  border-radius: var(--os1-radius);
  isolation: isolate;
}

.art__svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.art__chip {
  position: absolute;
  top: 0.85rem;
  left: 0.85rem;
  z-index: 2;
  padding: 0.3rem 0.55rem;
  background: rgba(20, 4, 4, 0.65);
  backdrop-filter: blur(8px);
  color: var(--os1-rose);
  font-size: 0.62rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  border: 1px solid rgba(245, 180, 163, 0.3);
  border-radius: 999px;
}

.art__name {
  position: absolute;
  left: 0.95rem;
  bottom: 0.95rem;
  right: 0.95rem;
  z-index: 2;
  font-family: var(--os1-font-sans);
  font-size: 0.62rem;
  letter-spacing: 0.32em;
  color: rgba(255, 235, 220, 0.55);
}
</style>
